import type {
  AddRelationshipAccountParams,
  AddRelationshipTagParams,
  BulkAddRelationshipTagsParams,
  BulkDeleteRelationshipsParams,
  BulkImportCreatorsParams,
  CreateBrandFitReportsParams,
  CreateCustomFieldsParams,
  CreateInformationRequestParams,
  CreateManualPaymentParams,
  CreateRelationshipsParams,
  CreateShopifyGiftOrderParams,
  DeleteCustomFieldParams,
  DeleteRelationshipTagParams,
  DeleteTagFromAllRelationshipsParams,
  DownloadCreatorDetailsParams,
  DownloadCreatorPostsParams,
  FetchAccountAvgWeeklyPostsParams,
  FetchAccountMetricsParams,
  FetchAccountParams,
  FetchAccountPostsParams,
  FetchAudienceMetricsParams,
  FetchBrandCreatorParams,
  FetchBrandCreatorsParams,
  FetchBrandedContentParams,
  FetchBrandRelationshipTagsParams,
  FetchCompetitiveMentionPostsParams,
  FetchCompetitiveMentionsParams,
  FetchCreatorAiInsightsParams,
  FetchCreatorsCountParams,
  FetchCustomFieldsParams,
  FetchInfluentialFansParams,
  FetchInformationRequestParams,
  FetchMetaPaidPartnersStatusParams,
  FetchNaturalLanguageSearchAccountsParams,
  FetchPastBrandPartnershipsParams,
  FetchShopifyGiftOrdersParams,
  FetchShopifyProductCategoriesParams,
  FetchShopifyProductListParams,
  FetchSimilarCreatorsParams,
  FindOrCreateCreatorsParams,
  GenerateCreatorAiInsightsParams,
  GetRelationshipTagsParams,
  RemoveRelationshipAccountParams,
  RequestMetaPaidPartnersParams,
  RevokeMetaPaidPartnersParams,
  SearchAccountsForTeaserParams,
  SearchAccountsParams,
  SearchCreatorsParams,
  SearchRelationshipsMultiBrandParams,
  SearchRelationshipsParams,
  ShopifyAccountParams,
  SubmitInformationRequestParams,
  UpdateCreatorDetailsParams,
  UpdateCustomFieldParams,
  UpdateInvitationStatusParams,
} from '@/apis/creators.types';
import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';

export type * from '@/apis/creators.types';

export const http = createDashHttpClient(env.creatorsApiUrl, {
  camelizeKeys: true,
});

export async function searchAccounts(
  {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    interests,
    paidInterests,
    countryCodes,
    locations,
    handle,
    visualFeatures,
    audience,
    languages,
    minAvgViews,
    maxAvgViews,
    minMedianViews,
    maxMedianViews,
    sortBy,
    minEngagementRate,
    maxEngagementRate,
    limit,
    afterCursor,
    competitiveMentions,
    minCreatorRate,
    maxCreatorRate,
    stateProvinces,
    gender,
    ageBucket,
    minEngagedAccounts,
    maxEngagedAccounts,
    instagram,
  }: SearchAccountsParams,
  config: HttpRequestConfig = {},
) {
  const url = `/accounts`;
  const payload = {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    interests,
    paidInterests,
    countryCodes,
    locations,
    handle,
    visualFeatures,
    audience,
    languages,
    minAvgViews,
    maxAvgViews,
    minMedianViews,
    maxMedianViews,
    sortBy,
    limit,
    minEngagementRate,
    maxEngagementRate,
    afterCursor,
    competitiveMentions,
    minCreatorRate,
    maxCreatorRate,
    stateProvinces,
    gender,
    ageBucket,
    minEngagedAccounts,
    maxEngagedAccounts,
    instagram,
  };
  return http.put(url, payload, { ...config });
}

export async function searchAccountsForTeaser(
  {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    predictedInterests,
    predictedLocations,
    handle,
    visualFeatures,
    sortBy,
  }: SearchAccountsForTeaserParams,
  config: HttpRequestConfig = {},
) {
  const url = `teaser/accounts`;
  const payload = {
    brandId,
    sources,
    followersGreaterThan,
    followersLessThan,
    bioIncludes,
    bioDoesNotInclude,
    captionsInclude,
    captionsDoNotInclude,
    bioOrCaptionsInclude,
    hasMentionedBrand,
    hasPostedBetween,
    predictedInterests,
    estimatedRegions: predictedLocations,
    handle,
    visualFeatures,
    sortBy,
  };
  return http.put(url, payload, { ...config });
}

export async function fetchAccount(
  { source, sourceCreatorId, handle, brandId }: FetchAccountParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}`, {
    ...config,
    params: { brandId, sourceCreatorId, handle },
  });
}

export async function fetchBrandCreators({
  url,
  brandId,
  creatorIds,
  postsPublishedStartDate,
  postsPublishedEndDate,
  instagramPostType,
  youtubePostType,
  afterCursor,
  limit,
}: FetchBrandCreatorsParams) {
  return http.put(url ?? `/brands/${brandId}/relationships`, {
    creatorIds,
    postsPublishedStartDate,
    postsPublishedEndDate,
    postType: instagramPostType || youtubePostType,
    afterCursor,
    limit,
  });
}

export async function fetchBrandCreator({ url, brandId, creatorId }: FetchBrandCreatorParams) {
  return http.get(url ?? `/brands/${brandId}/relationships/${creatorId}`);
}

export async function updateCreatorDetails({
  url,
  brandId,
  creatorId,
  creatorDetails,
}: UpdateCreatorDetailsParams) {
  return http.put(url ?? `/brands/${brandId}/relationships/${creatorId}/details`, creatorDetails);
}

export async function fetchBrandRelationshipTags({
  url,
  brandId,
}: FetchBrandRelationshipTagsParams) {
  return http.get(url ?? `/brands/${brandId}/relationships/tags`);
}

export async function createInformationRequest({
  brandId,
  creatorId,
}: CreateInformationRequestParams) {
  return http.put(`/brands/${brandId}/relationships/${creatorId}/information_requests`);
}

export async function fetchInformationRequest({ token }: FetchInformationRequestParams) {
  return http.get(`/information_requests?token=${token}`);
}

export async function submitInformationRequest(
  { token, creatorData }: SubmitInformationRequestParams,
  config: HttpRequestConfig = {},
) {
  const payload = { token, ...creatorData };
  return http.put(`/information_requests`, payload, { ...config });
}

export async function bulkImportCreators(
  { brandId, creators }: BulkImportCreatorsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(`/brands/${brandId}/relationships/bulk_imports`, { creators }, { ...config });
}

export async function bulkDeleteRelationships(
  { brandId, creatorIds }: BulkDeleteRelationshipsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(`/brands/${brandId}/relationships/bulk_delete`, { creatorIds }, { ...config });
}

export async function addRelationshipTag(
  { brandId, creatorId, name, color }: AddRelationshipTagParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/brands/${brandId}/relationships/${creatorId}/tags`,
    { name, color },
    { ...config },
  );
}

export async function bulkAddRelationshipTags(
  { brandId, creatorIds, tags }: BulkAddRelationshipTagsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(`/brands/${brandId}/relationships/tags`, { creatorIds, tags }, { ...config });
}

export async function deleteRelationshipTag(
  { brandId, creatorId, tagId }: DeleteRelationshipTagParams,
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/relationships/${creatorId}/tags/${tagId}`, {
    ...config,
  });
}

export async function deleteTagFromAllRelationships(
  { brandId, tagId }: DeleteTagFromAllRelationshipsParams,
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/relationships/tags/${tagId}`, {
    ...config,
  });
}

export async function searchRelationshipsMultiBrand(
  { brandIds, limit = 60 }: SearchRelationshipsMultiBrandParams,
  config: HttpRequestConfig = {},
) {
  const payload = {
    brand_ids: brandIds,
    limit,
  };

  return http.put('/relationships', payload, { ...config });
}

export async function updateInvitationStatus({
  source,
  sourceCreatorId,
  invitedByBrandId,
  invitationStatus,
  emailContent,
}: UpdateInvitationStatusParams) {
  const params = {
    source,
    invited_by_brand_id: invitedByBrandId,
    source_creator_id: sourceCreatorId,
    invitation_status: invitationStatus,
    email_content: emailContent,
  };

  return http.put(`/accounts/invitations`, params);
}

export async function searchCreators(
  {
    brandId,
    limit,
    sort,
    lookupName,
    restrictToPlatforms,
    postsPublishedStartDate,
    postsPublishedEndDate,
    tagFilterType,
    tags,
    creatorLocations,
    hasMentionedBrand,
    creatorIds,
    instagramPostType,
    instagramConnectionStatus,
    instagramFollowersGreaterThan,
    instagramFollowersLessThan,
    instagramInterests,
    instagramIsBusiness,
    instagramHandle,
    tiktokConnectionStatus,
    tiktokFollowersGreaterThan,
    tiktokFollowersLessThan,
    tiktokMinEngagementRate,
    tiktokMaxEngagementRate,
    tiktokTTCMStatus,
    tiktokOrganicInterests,
    tiktokPaidInterests,
    tiktokAudienceGender,
    tiktokAudienceAge,
    tiktokMinCreatorRate,
    tiktokMaxCreatorRate,
    tiktokCreatorCurrency,
    tiktokHandle,
    tiktokMinMedianViews,
    tiktokMaxMedianViews,
    youtubeHandle,
    youtubePostType,
    youtubeFollowersGreaterThan,
    youtubeFollowersLessThan,
    youtubeVideoCountGreaterThan,
    youtubeVideoCountLessThan,
    youtubeViewsGreaterThan,
    youtubeViewsLessThan,
    competitiveMentions,
    afterCursor,
  }: SearchCreatorsParams,
  config: HttpRequestConfig = {},
) {
  const accounts = {
    instagram: {
      status: instagramConnectionStatus,
      followersGreaterThan: instagramFollowersGreaterThan,
      followersLessThan: instagramFollowersLessThan,
      interests: instagramInterests,
      isBusiness: instagramIsBusiness,
      handle: instagramHandle,
    },
    tiktok: {
      status: tiktokConnectionStatus,
      followersGreaterThan: tiktokFollowersGreaterThan,
      followersLessThan: tiktokFollowersLessThan,
      minEngagementRate: tiktokMinEngagementRate,
      maxEngagementRate: tiktokMaxEngagementRate,
      ttcmStatus: tiktokTTCMStatus,
      handle: tiktokHandle,
      interests: tiktokOrganicInterests,
      paidInterests: tiktokPaidInterests,
      minCreatorRate: tiktokMinCreatorRate,
      maxCreatorRate: tiktokMaxCreatorRate,
      creatorCurrency: tiktokCreatorCurrency,
      minMedianViews: tiktokMinMedianViews,
      maxMedianViews: tiktokMaxMedianViews,
      audience: {
        gender: tiktokAudienceGender,
        age: tiktokAudienceAge,
      },
    },
    youtube: {
      handle: youtubeHandle,
      followersGreaterThan: youtubeFollowersGreaterThan,
      followersLessThan: youtubeFollowersLessThan,
      videoCountGreaterThan: youtubeVideoCountGreaterThan,
      videoCountLessThan: youtubeVideoCountLessThan,
      viewsGreaterThan: youtubeViewsGreaterThan,
      viewsLessThan: youtubeViewsLessThan,
    },
  };

  const postType =
    ((instagramPostType?.length ?? 0) > 0 ? instagramPostType : undefined) ||
    ((youtubePostType?.length ?? 0) > 0 ? youtubePostType : undefined);

  return http.put(
    `/brands/${brandId}/relationships`,
    {
      limit,
      sort,
      lookupName,
      restrictToPlatforms,
      postsPublishedStartDate,
      postsPublishedEndDate,
      tagFilterType,
      tags,
      locations: creatorLocations,
      hasMentionedBrand,
      creatorIds,
      postType,
      accounts,
      competitiveMentions,
      afterCursor,
    },
    { ...config },
  );
}

export async function findOrCreateCreators(
  { brandId, creators }: FindOrCreateCreatorsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(`/brands/${brandId}/creators`, creators, { ...config });
}

export async function createRelationships({ url, brandId, creatorIds }: CreateRelationshipsParams) {
  return http.post(url ?? `/brands/${brandId}/relationships`, { creatorIds });
}

export async function getRelationshipTags(
  { brandIds, limit = 60, offset = 0, nextUrl = null }: GetRelationshipTagsParams,
  config: HttpRequestConfig = {},
) {
  if (!Array.isArray(brandIds) || brandIds.length === 0) {
    throw new Error('brandIds must be a non-empty array');
  }

  const params = {
    brand_ids: brandIds.join(','),
    limit,
    offset,
  };

  if (nextUrl) return http.get(nextUrl, config);

  return http.get('/relationships/tags', {
    params,
    ...config,
  });
}

export async function searchRelationships(
  params: SearchRelationshipsParams,
  config: HttpRequestConfig = {},
) {
  const { brandIds, limit = 60, afterCursor = null } = params;

  if (!Array.isArray(brandIds) || brandIds.length === 0) {
    throw new Error('brandIds must be a non-empty array');
  }

  const payload: Record<string, unknown> = {
    brand_ids: brandIds,
    limit,
    after_cursor: afterCursor,
  };

  Object.keys(payload).forEach((key) => {
    if (payload[key] === null || payload[key] === undefined) {
      delete payload[key];
    }
  });
  return http.put('/relationships', payload, { ...config });
}

export async function fetchCreatorsCount({ brandId }: FetchCreatorsCountParams) {
  return http.put(`/brands/${brandId}/relationships`);
}

export async function fetchAccountPosts(
  {
    brandId,
    source,
    handle,
    cache,
    limit,
    afterCursor,
    includeAuthorized,
    includeAllMetrics,
  }: FetchAccountPostsParams,
  config: HttpRequestConfig = {},
) {
  return http.get('/posts', {
    ...config,
    params: {
      brandId,
      source,
      handle,
      cache,
      limit,
      afterCursor,
      includeAuthorized,
      includeAllMetrics,
    },
  });
}

export async function fetchAccountMetrics(
  { source, handle, brandId }: FetchAccountMetricsParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}/metrics`, {
    ...config,
    params: { brandId, handle },
  });
}

export async function fetchAccountAvgWeeklyPosts(
  { source, handle, brandId }: FetchAccountAvgWeeklyPostsParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}/metrics/avg_weekly_posts`, {
    ...config,
    params: { brandId, handle },
  });
}

export async function fetchAudienceMetrics(
  { source, handle, brandId, metric }: FetchAudienceMetricsParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}/audience-metrics`, {
    ...config,
    params: { brandId, handle, metric },
  });
}

export async function fetchSimilarCreators(
  { brandId, source, handle, sourceCreatorId }: FetchSimilarCreatorsParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}/similar`, {
    ...config,
    params: { brandId, handle, sourceCreatorId },
  });
}

export async function addRelationshipAccount({
  brandId,
  creatorId,
  handle,
  source,
}: AddRelationshipAccountParams) {
  return http.post(`/brands/${brandId}/relationships/add_account`, {
    creatorId,
    handle,
    source,
  });
}

export async function removeRelationshipAccount({
  brandId,
  creatorId,
  source,
}: RemoveRelationshipAccountParams) {
  return http.post(`/brands/${brandId}/relationships/remove_account`, {
    creatorId,
    source,
  });
}

export async function fetchCustomFields(
  { brandId }: FetchCustomFieldsParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/relationships/custom_fields`, {
    ...config,
    params: {},
  });
}

export async function createCustomFields(
  { brandId, customFields }: CreateCustomFieldsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/brands/${brandId}/relationships/custom_fields`,
    { customFields },
    { ...config },
  );
}

export async function updateCustomField(
  { brandId, fieldId, name, showInRequestForm }: UpdateCustomFieldParams,
  config: HttpRequestConfig = {},
) {
  return http.put(
    `/brands/${brandId}/relationships/custom_fields/${fieldId}`,
    { name, showInRequestForm },
    { ...config },
  );
}

export async function deleteCustomField(
  { brandId, fieldId }: DeleteCustomFieldParams,
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/relationships/custom_fields/${fieldId}`, {
    ...config,
  });
}

export async function fetchMetaPaidPartnersStatus(
  { handle, brandId, brandInstagramId }: FetchMetaPaidPartnersStatusParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/meta_paid_partners`, {
    ...config,
    params: { brandInstagramId, handle },
  });
}

export async function requestMetaPaidPartners(
  { handle, brandId, brandInstagramId }: RequestMetaPaidPartnersParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/brands/${brandId}/meta_paid_partners`,
    { brandInstagramId, handle },
    { ...config },
  );
}

export async function revokeMetaPaidPartners(
  { handle, brandId, brandInstagramId, creatorInstagramId }: RevokeMetaPaidPartnersParams,
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/meta_paid_partners`, {
    ...config,
    params: { brandInstagramId, creatorInstagramId, handle },
  });
}

export async function createManualPayment({
  brandId,
  tiktokHandle,
  instagramHandle,
  campaignName,
  campaignStartDate,
  campaignEndDate,
  recipientType,
  recipientFirstName,
  recipientLastName,
  recipientBusinessName,
  recipientEmail,
  currency,
  amount,
}: CreateManualPaymentParams) {
  return http.post('/payments/manual-payments', {
    brandId,
    tiktokHandle,
    instagramHandle,
    campaignName,
    campaignStartDate,
    campaignEndDate,
    recipientType,
    recipientFirstName,
    recipientLastName,
    recipientBusinessName,
    recipientEmail,
    currency,
    amount,
  });
}

export async function downloadCreatorDetails({
  brandId,
  socketId,
  creatorIds,
  hasMentionedBrand,
  instagramConnectionStatus,
  instagramFollowersGreaterThan,
  instagramFollowersLessThan,
  instagramIsBusiness,
  instagramInterests,
  instagramPostType,
  lookupName,
  postsPublishedEndDate,
  postsPublishedStartDate,
  restrictToPlatforms,
  sort,
  tagFilterType,
  tags,
  tiktokAudienceAge,
  tiktokAudienceGender,
  tiktokAudienceRegions,
  tiktokConnectionStatus,
  tiktokFollowersGreaterThan,
  tiktokFollowersLessThan,
  tiktokTTCMStatus,
  tiktokMinCreatorRate,
  tiktokMaxCreatorRate,
  tiktokCreatorCurrency,
  tiktokMinMedianViews,
  tiktokMaxMedianViews,
  tiktokMinEngagementRate,
  tiktokMaxEngagementRate,
  tiktokPaidInterests,
  tiktokOrganicInterests,
  campaignName,
}: DownloadCreatorDetailsParams) {
  return http.post(`/brands/${brandId}/relationships/creator_details_csv`, {
    socketId,
    creatorIds,
    hasMentionedBrand,
    lookupName,
    postType: instagramPostType,
    postsPublishedEndDate,
    postsPublishedStartDate,
    restrictToPlatforms,
    sort,
    tagFilterType,
    tags,
    campaignName,
    accounts: {
      instagram: {
        status: instagramConnectionStatus,
        followersGreaterThan: instagramFollowersGreaterThan,
        followersLessThan: instagramFollowersLessThan,
        interests: instagramInterests,
        isBusiness: instagramIsBusiness,
      },
      tiktok: {
        status: tiktokConnectionStatus,
        followersGreaterThan: tiktokFollowersGreaterThan,
        followersLessThan: tiktokFollowersLessThan,
        ttcmStatus: tiktokTTCMStatus,
        minCreatorRate: tiktokMinCreatorRate,
        maxCreatorRate: tiktokMaxCreatorRate,
        creatorCurrency: tiktokCreatorCurrency,
        minMedianViews: tiktokMinMedianViews,
        maxMedianViews: tiktokMaxMedianViews,
        minEngagementRate: tiktokMinEngagementRate,
        maxEngagementRate: tiktokMaxEngagementRate,
        paidInterests: tiktokPaidInterests,
        interests: tiktokOrganicInterests,
        audience: {
          regions: tiktokAudienceRegions,
          gender: tiktokAudienceGender,
          age: tiktokAudienceAge,
        },
      },
    },
  });
}

export async function downloadCreatorPosts({
  brandId,
  socketId,
  creatorIds,
  creatorLocations,
  hasMentionedBrand,
  instagramConnectionStatus,
  instagramFollowersGreaterThan,
  instagramFollowersLessThan,
  instagramIsBusiness,
  instagramInterests,
  instagramPostType,
  lookupName,
  postsPublishedEndDate,
  postsPublishedStartDate,
  restrictToPlatforms,
  sort,
  tagFilterType,
  tags,
  tiktokAudienceAge,
  tiktokAudienceGender,
  tiktokAudienceRegions,
  tiktokConnectionStatus,
  tiktokFollowersGreaterThan,
  tiktokFollowersLessThan,
  tiktokMinEngagementRate,
  tiktokMaxEngagementRate,
  tiktokInterests,
  tiktokTTCMStatus,
  ttoCampaignId,
}: DownloadCreatorPostsParams) {
  return http.put(`/brands/${brandId}/relationships/export_posts`, {
    socketId,
    creatorIds,
    sort,
    lookupName,
    restrictToPlatforms,
    postsPublishedStartDate,
    postsPublishedEndDate,
    tagFilterType,
    tags,
    creatorLocations,
    hasMentionedBrand,
    ttoCampaignId,
    postType: instagramPostType,
    accounts: {
      instagram: {
        status: instagramConnectionStatus,
        followersGreaterThan: instagramFollowersGreaterThan,
        followersLessThan: instagramFollowersLessThan,
        interests: instagramInterests,
        isBusiness: instagramIsBusiness,
      },
      tiktok: {
        status: tiktokConnectionStatus,
        followersGreaterThan: tiktokFollowersGreaterThan,
        followersLessThan: tiktokFollowersLessThan,
        minEngagementRate: tiktokMinEngagementRate,
        maxEngagementRate: tiktokMaxEngagementRate,
        ttcmStatus: tiktokTTCMStatus,
        interests: tiktokInterests,
        audience: {
          regions: tiktokAudienceRegions,
          gender: tiktokAudienceGender,
          age: tiktokAudienceAge,
        },
      },
    },
  });
}

export const fetchShopifyProductList = ({
  brandId,
  limit,
  name,
  sku,
  nameOrSku,
  category,
  type,
  price,
  inventoryCount,
  afterCursor,
}: FetchShopifyProductListParams) =>
  http.put(`/brands/${brandId}/shopify/products`, {
    limit,
    filters: {
      name,
      sku,
      nameOrSku,
      category,
      type,
      price,
      inventoryCount,
    },
    afterCursor,
  });

export const fetchShopifyProductCategories = ({ brandId }: FetchShopifyProductCategoriesParams) =>
  http.get(`/brands/${brandId}/shopify/product_categories`);

export const fetchShopifyProductTypes = ({ brandId }: FetchShopifyProductCategoriesParams) =>
  http.get(`/brands/${brandId}/shopify/product_types`);

export async function fetchShopifyGiftOrders(
  {
    brandId,
    creatorIds,
    orderIds,
    summary = false,
    includeDrafts = false,
    limit,
    offset,
    campaignId,
  }: FetchShopifyGiftOrdersParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/shopify/orders`, {
    ...config,
    params: {
      campaignId,
      creatorIds: creatorIds?.join(','),
      orderIds: orderIds?.join(','),
      summary,
      includeDrafts,
      limit,
      offset,
    },
  });
}

export async function createShopifyGiftOrder(
  { brandId, creatorIds, productVariants, campaignId }: CreateShopifyGiftOrderParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/brands/${brandId}/shopify/orders`,
    { creatorIds, productVariants, campaignId },
    { ...config },
  );
}

export async function disconnectShopifyAccount(
  { brandId }: ShopifyAccountParams,
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/shopify/webhooks`, config);
}

export async function connectShopifyAccount(
  { brandId }: ShopifyAccountParams,
  config: HttpRequestConfig = {},
) {
  return http.post(`/brands/${brandId}/shopify/connected`, config);
}

export async function fetchInfluentialFans(
  { brandId, source, sort, limit, afterCursor }: FetchInfluentialFansParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}/brands/${brandId}/influential_fans`, {
    ...config,
    params: {
      sort,
      limit,
      afterCursor,
    },
  });
}

export async function fetchCompetitiveMentions(
  {
    brandId,
    creatorId,
    limit,
    offset,
    sort,
    sources,
    competitorIds,
    isKnownCompetitor,
    excludedBrandAccountIds,
  }: FetchCompetitiveMentionsParams,
  config: HttpRequestConfig = {},
) {
  const payload = {
    sources,
    competitorIds,
    isKnownCompetitor,
    excludedBrandAccountIds,
  };

  return http.put(`/brands/${brandId}/relationships/${creatorId}/competitive_mentions`, payload, {
    ...config,
    params: { limit, offset, sort },
  });
}

export async function fetchCompetitiveMentionPosts(
  {
    brandId,
    creatorId,
    sources,
    competitorIds,
    limit,
    offset,
    excludedBrandAccountIds,
  }: FetchCompetitiveMentionPostsParams,
  config: HttpRequestConfig = {},
) {
  const payload = {
    sources,
    competitorIds,
    excludedBrandAccountIds,
  };

  return http.put(
    `/brands/${brandId}/relationships/${creatorId}/competitive_mentions/posts`,
    payload,
    {
      ...config,
      params: { limit, offset },
    },
  );
}

export async function fetchBrandedContent(
  { brandId, handle, source }: FetchBrandedContentParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}/branded_content`, {
    ...config,
    params: { brandId, handle },
  });
}

export async function fetchCreatorAiInsights(
  { brandId, creatorId }: FetchCreatorAiInsightsParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/relationships/${creatorId}/ai_profile_insights`, {
    ...config,
  });
}

export async function generateCreatorAiInsights(
  { brandId, creatorId }: GenerateCreatorAiInsightsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/brands/${brandId}/relationships/${creatorId}/ai_profile_insights`,
    {},
    {
      ...config,
    },
  );
}

export async function fetchPastBrandPartnerships(
  { brandId, handle, source }: FetchPastBrandPartnershipsParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/accounts/${source}/past_brand_partnerships`, {
    ...config,
    params: { brandId, handle },
  });
}

export async function fetchNaturalLanguageSearchAccounts(
  {
    brandId,
    source,
    userRequest,
    visualFeatures,
    restrictToDataSource,
    restrictToDataSourceReason,
  }: FetchNaturalLanguageSearchAccountsParams,
  config: HttpRequestConfig = {},
) {
  return http.get('/natural_language_search/accounts', {
    ...config,
    params: {
      brandId,
      source,
      userRequest,
      visualFeatures,
      restrictToDataSource,
      restrictToDataSourceReason,
    },
  });
}

export async function createBrandFitReports(
  { brandId, source, sourceCreatorId, forceRegeneration = false }: CreateBrandFitReportsParams,
  config: HttpRequestConfig = {},
) {
  return http.post(
    '/accounts/brand_fit_reports',
    {
      accounts: [{ brandId, source, sourceCreatorId, forceRegeneration }],
    },
    { ...config },
  );
}
